import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  Grid,
  SingleSelectionRadio,
  TextFieldForm,
  Typography,
} from 'web-library';


import { makeStyles } from 'tss-react/mui';
import dayjs from 'dayjs';
// Removed import for ParsableDate as it is not exported by @mui/x-date-pickers
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { TFunction } from 'i18next';
import { MutationStatus } from 'react-query';
import { AxiosError } from 'axios';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import ControlledAutocomplete from '../../../../components/form-items/ControlledAutocomplete';
import { ExamSchoolFormData } from '../../../../models';
import { useExamSchoolingDetails } from '../../logic/useExamSchoolingDetails';
import SiblingInfoTable from '../SiblingInfoTable';
import SchoolInfoTable from '../SchoolInfoTable';
import { GenericErrorResponse, KeyValueResponse } from '../../../../api-refactored/typings';
import { AdmissionExamLevelResponse, IOptionType } from '../../../../api/candidate/exam/model';
import { GetNonNationalExamSchoolingDetailsResponse } from '../../../../api-refactored/non-national-exam-registration/useGetNonNationalExamSchoolingDetails';
import { ExamSchoolingDetailsPageLocation } from '../../../../pages/examRegistration/NonNationalExam/AEIS/ExamSchoolingDetails';
import NumberOnlyFormItem from '../../../../components/form-items/NumberOnlyFormItem';
import AdmissionLevelSection from './AdmissionLevelSection';
import { NonNationalApplicationStatusResponse } from '../../../../api-refactored/non-national-exam-registration/useGetAdmissionLevel';
import MotherTongueLanguageSection from './MotherTongueLanguageSection';
import { useGetUserAuthorities } from '../../../../api-refactored/user/useGetUserAuthorities';
import { existInArray } from '../../../../utils/ArrayUtils';
import { useGetValidCeqTestDates } from '../../../../api-refactored/non-national-exam-registration/useGetValidCeqTestDates';
import ControlledDatePicker from "web-library/stories/atom/DatePicker/ControlledDatePicker";

const REF_NO_TEXT = 'exam->>NonNationalExam.CentreReferenceNo';
const VERIFICATION_NO_TEXT = 'exam->>NonNationalExam.VerificationNo';
const ENTER_VALUE_TEXT = 'exam->>PlaceHolders.EnterValue';

const useStyles = makeStyles()((theme) =>
({
  container: {
    marginTop: theme.spacing(-2),
  },
  input: {
    marginBottom: theme.spacing(4),
  },
  inputLeft: {
    marginBottom: theme.spacing(4),
    paddingRight: theme.spacing(1),
  },
  inputRight: {
    marginBottom: theme.spacing(4),
    paddingLeft: theme.spacing(1),
  },
  marginSection: {
    marginBottom: theme.spacing(4),
  },
  subject: {
    marginTop: theme.spacing(4),
    color: theme.color.text.textHeader,
  },
  subjectValue: {
    marginBottom: theme.spacing(6),
  },
  require: {
    color: theme.color.state.stateImportant,
  },
  buttonContainer: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(12),
  },
  backButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(2),
    },
  },
  submitButton: {
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(2),
    },
  },
  errorText: {
    color: theme.color.state.stateError,
  },
  LockedMessage: {
    color: theme.color.state.stateError,
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle2: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(4),
  },
  marginBottomDivider: {
    marginBottom: theme.spacing(2),
  },
  siblingRegisteringQuest: {
    marginTop: theme.spacing(4),
    color: theme.color.text.textHeader,
  },
  errString: {
    color: theme.color.state.stateError,
    marginTop: theme.spacing(-2),
    marginBottom: theme.spacing(2),
  },
}));

export const datePickerFormatConvertor = (date: string) =>
  dayjs(date, 'ddd, DD MMM YYYY hh:mm:ss').format('YYYY-MM-DD HH:mm:ss');

interface CAECertificateDetailsProps {
  registrationUuid: string;
  registrationExamLevel: KeyValueResponse;
  formHook: UseFormReturn<ExamSchoolFormData>;
  dateOfCEQ: string;
  ceqData: KeyValueResponse[] | undefined;
  birthCountryData: IOptionType[] | undefined;
  hasEditAccess: boolean;
  setValidCeqTestDates: React.Dispatch<React.SetStateAction<ValidCeqTestDatesModel | undefined>>;
  isFullForm: boolean;
}

const CAECertificateDetails = ({
  registrationUuid,
  registrationExamLevel,
  formHook,
  dateOfCEQ,
  ceqData,
  birthCountryData,
  hasEditAccess,
  setValidCeqTestDates,
  isFullForm
}: CAECertificateDetailsProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { control, formState: { errors }, setValue, trigger } = formHook;
  const { touchedFields } = formHook.formState;

  const { isLoading: isLoadingValidCeqTestDateListing } = useGetValidCeqTestDates({
    registrationExamLevelUuid: registrationExamLevel.code,
    registrationUuid,
    onSuccess: (data) => {
      setValidCeqTestDates(data);
    },
    onError: (err) => {
      // do nothing
      formHook.setError('dateOfCEQ', {
        type: 'manual',
        message: err?.response?.data.message,
      });
    },
    enabled: touchedFields?.dateOfCEQ as boolean || !dateOfCEQ || isFullForm,
  });

  return (
    <>
      <Typography className={classes.sectionTitle} variant="h3">
        {t('exam->>NonNationalExam.CAECertificateDetails')}
      </Typography>
      <ControlledAutocomplete
        className={classes.input}
        id="CEQTypes"
        name="ceqType"
        control={control}
        label={t('exam->>NonNationalExam.CEQType')}
        subLabel={t('exam->>NonNationalExam.CEQTypeHelper')}
        isSubLabelBelowLabel
        options={ceqData || []}
        getOptionLabel={(option) => typeof option === 'string' ? option : option.description}
        error={!!errors?.ceqType}
        helperText={errors.ceqType?.message?.toString()}
        isRequired
        disableClearable
        readOnly
        disabled={!hasEditAccess}
      />
      <TextFieldForm
        className={classes.input}
        fullWidth
        variant="outlined"
        id="centerReferenceNo"
        name="centerReferenceNo"
        error={!!errors.centerReferenceNo}
        helperText={errors.centerReferenceNo?.message?.toString()}
        title={t(REF_NO_TEXT)}
        placeholder={t('exam->>PlaceHolders.EnterCentreReferenceNo')}
        label={t(REF_NO_TEXT)}
        form={formHook}
        isRequired
        disabled={!hasEditAccess}
      />
      <TextFieldForm
        className={classes.input}
        fullWidth
        variant="outlined"
        id="verificationNo"
        name="verificationNo"
        error={!!errors.verificationNo}
        helperText={errors.verificationNo?.message?.toString()}
        title={t(VERIFICATION_NO_TEXT)}
        placeholder={t('exam->>PlaceHolders.EnterVerificationNo')}
        label={t(VERIFICATION_NO_TEXT)}
        form={formHook}
        isRequired
        disabled={!hasEditAccess}
      />
      <ControlledAutocomplete
        className={classes.input}
        id="inputSelect-countryCEQ"
        name="countryCEQ"
        control={control}
        label={t('exam->>PlaceHolders.SelectCountryCEQ')}
        placeholder={t('exam->>PlaceHolders.SelectCountryCEQ')}
        options={birthCountryData || []}
        getOptionLabel={(option) => typeof option === 'string' ? option : option.description}
        error={!!errors?.countryCEQ}
        helperText={errors?.countryCEQ?.message?.toString()}
        isRequired
        disableClearable
        readOnly
        disabled={!hasEditAccess}
      />
      <ControlledDatePicker
        id="dateOfCEQ"
        name="dateOfCEQ"
        className={classes.input}
        label={t('exam->>NonNationalExam.DateOfCEQTaken')}
        placeholder={t('exam->>PlaceHolders.SelectDateOfCEQTaken')}
        value={dateOfCEQ || null}
        onChange={(date) => {
          setValue('dateOfCEQ', date || '');
          trigger('dateOfCEQ');
        }}
        disableFuture
        error={!!errors.dateOfCEQ}
        helperText={errors.dateOfCEQ?.message?.toString()}
        inputVariant="filled"
        autoOk
        isRequired
        disabled={!hasEditAccess || isLoadingValidCeqTestDateListing}
        control={control}
      />
    </>
  );
};

interface CEQScoresProps {
  formHook: UseFormReturn<ExamSchoolFormData>;
  hasEditAccess: boolean;
}

const CEQScores = ({ formHook, hasEditAccess }: CEQScoresProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { formState: { errors } } = formHook;

  return (
    <>
      <Typography className={classes.sectionTitle2} variant="h3">
        {t('exam->>NonNationalExam.CEQScores')}
      </Typography>
      <Grid md={6}>
        <NumberOnlyFormItem
          className={classes.inputLeft}
          fullWidth
          variant="outlined"
          id="overall"
          name="overall"
          error={!!errors.overall}
          helperText={errors.overall?.message?.toString()}
          title={t('exam->>NonNationalExam.Overall')}
          placeholder={t(ENTER_VALUE_TEXT)}
          label={t('exam->>NonNationalExam.Overall')}
          form={formHook}
          isRequired
          inputProps={{ maxLength: 3 }}
          shouldValidate={false}
          disabled={!hasEditAccess}
        />
      </Grid>
      <Grid container direction="row" md={12}>
        <Grid md={6} xs={6}>
          <NumberOnlyFormItem
            className={classes.inputLeft}
            fullWidth
            variant="outlined"
            id="reading"
            name="reading"
            error={!!errors?.reading}
            helperText={errors?.reading?.message?.toString()}
            title={t('exam->>NonNationalExam.Reading')}
            placeholder={t(ENTER_VALUE_TEXT)}
            label={t('exam->>NonNationalExam.Reading')}
            form={formHook}
            isRequired
            inputProps={{ maxLength: 3 }}
            shouldValidate={false}
            disabled={!hasEditAccess}
          />
        </Grid>
        <Grid md={6} xs={6}>
          <NumberOnlyFormItem
            className={classes.inputRight}
            fullWidth
            variant="outlined"
            id="listening"
            name="listening"
            error={!!errors?.listening}
            helperText={errors?.listening?.message?.toString()}
            title={t('exam->>NonNationalExam.Listening')}
            placeholder={t(ENTER_VALUE_TEXT)}
            label={t('exam->>NonNationalExam.Listening')}
            form={formHook}
            isRequired
            inputProps={{ maxLength: 3 }}
            shouldValidate={false}
            disabled={!hasEditAccess}
          />
        </Grid>
      </Grid>
      <Grid container direction="row" md={12} className={classes.marginBottomDivider}>
        <Grid md={6} xs={6}>
          <NumberOnlyFormItem
            className={classes.inputLeft}
            fullWidth
            variant="outlined"
            id="writing"
            name="writing"
            error={!!errors?.writing}
            helperText={errors?.writing?.message?.toString()}
            title={t('exam->>NonNationalExam.Writing')}
            placeholder={t(ENTER_VALUE_TEXT)}
            label={t('exam->>NonNationalExam.Writing')}
            form={formHook}
            isRequired
            inputProps={{ maxLength: 3 }}
            shouldValidate={false}
            disabled={!hasEditAccess}
          />
        </Grid>
        <Grid md={6} xs={6}>
          <NumberOnlyFormItem
            className={classes.inputRight}
            fullWidth
            variant="outlined"
            id="speaking"
            name="speaking"
            error={!!errors?.speaking}
            helperText={errors?.speaking?.message?.toString()}
            title={t('exam->>NonNationalExam.Speaking')}
            placeholder={t(ENTER_VALUE_TEXT)}
            label={t('exam->>NonNationalExam.Speaking')}
            form={formHook}
            isRequired
            inputProps={{ maxLength: 3 }}
            shouldValidate={false}
            disabled={!hasEditAccess}
          />
        </Grid>
      </Grid>
    </>
  );
};

interface RadioButtonViewProps {
  title: string;
  t: TFunction;
  classes: Record<string,string>;
  name: keyof ExamSchoolFormData;
  hasValue: string;
  setHas: Dispatch<SetStateAction<string>>;
  setEmptyMessage: Dispatch<SetStateAction<string>>;
  formHook: UseFormReturn<ExamSchoolFormData>;
  hasEditAccess: boolean;
}

const RadioButtonView = ({
  classes,
  formHook,
  name,
  hasValue,
  setHas,
  t,
  title,
  setEmptyMessage,
  hasEditAccess,
}: RadioButtonViewProps) => {
  const { setValue, control } = formHook;
  return (
    <>
      <Typography variant="h6" className={classes.subject}>
        {title}
        <Typography display="inline" variant="h6" className={classes.require}>
          {' '}
          *
        </Typography>
      </Typography>
      <SingleSelectionRadio
        name={name}
        color="primary"
        value={hasValue}
        disabled={!hasEditAccess}
        onChange={(event) => {
          setHas(event.target.value);
          setValue(name, event.target.value);
          setEmptyMessage('');
        }}
        data={[
          { label: t('exam->>NonNationalExam.Yes'), value: 'yes' },
          { label: t('exam->>NonNationalExam.No'), value: 'no' },
        ]}
        control={control}
      />
    </>
  );
};

interface ButtonContainerProps {
  classes: Record<string,string>;
  t: TFunction;
  examSchoolingDetailsData: GetNonNationalExamSchoolingDetailsResponse;
  goBackClick: () => void;
  postExamSchoolingDetailStatus: MutationStatus;
  submitButtonClick: () => void;
  createErrorMessage: string;
  isErrorPost: boolean;
  postError: AxiosError<GenericErrorResponse> | null;
}

const ButtonContainer = ({
  classes,
  t,
  examSchoolingDetailsData,
  goBackClick,
  postExamSchoolingDetailStatus,
  submitButtonClick,
  createErrorMessage,
  isErrorPost,
  postError,
}: ButtonContainerProps) => (
  <Box className={classes.buttonContainer}>
    {examSchoolingDetailsData.lockedByUser && (
      <Typography variant="body2" className={classes.LockedMessage}>
        {t('exam->>NonNationalExam.LockedMessage', {
          lockedByUser: examSchoolingDetailsData.lockedByUser,
        }).toString()}
      </Typography>
    )}
    {createErrorMessage && (
      <Typography variant="body2" className={classes.errString}>
        {createErrorMessage}
      </Typography>
    )}
    {isErrorPost && (
      <Typography variant="body2" className={classes.errString}>
        {postError?.response?.data.message || postError?.message}
      </Typography>
    )}
    <Button
      id="button-back"
      className={classes.backButton}
      variant="outlined"
      onClick={goBackClick}
    >
      {t('components->>CompleteProfileDetails.Back').toString()}
    </Button>
    <ApiButton
      id="button-confirm-exam-schooling"
      className={classes.submitButton}
      status={postExamSchoolingDetailStatus}
      onClick={submitButtonClick}
    >
      {t('exam->>NonNationalExam.ConfirmExamSchooling').toString()}
    </ApiButton>
  </Box>
);

const checkCountryDefault = ({
  examSchoolingDetailsData,
  birthCountryData,
}: {
  examSchoolingDetailsData: GetNonNationalExamSchoolingDetailsResponse | undefined;
  birthCountryData: IOptionType[] | undefined;
}) =>
  examSchoolingDetailsData && examSchoolingDetailsData.caeCertificateDetailsModel
    ? birthCountryData?.find(
      (v) => v.code === examSchoolingDetailsData.caeCertificateDetailsModel.country.code
    )
    : undefined;

const checkCeqTypeDefault = (
  examSchoolingDetailsData: GetNonNationalExamSchoolingDetailsResponse | undefined,
  ceqData: KeyValueResponse[] | undefined
) =>
  examSchoolingDetailsData?.caeCertificateDetailsModel &&
    examSchoolingDetailsData?.caeCertificateDetailsModel.ceqTypeUuid
    ? ceqData?.find(
      (v) => v.code === examSchoolingDetailsData?.caeCertificateDetailsModel.ceqTypeUuid
    )
    : undefined;

interface ExamSchoolingDetailsFormProps {
  ceqData: KeyValueResponse[] | undefined;
  examSchoolingDetailsData: GetNonNationalExamSchoolingDetailsResponse;
  birthCountryData: IOptionType[] | undefined;
  admissionLevelData: NonNationalApplicationStatusResponse;
  siblingExamLevelData: IOptionType[] | undefined;
  examTypesData: AdmissionExamLevelResponse;
  isOpsAdmin?: boolean;
}

export interface ValidCeqTestDatesModel {
  firstAcceptedCeqTestDate: string;
  lastAcceptedCeqTestDate: string;
}

const ExamSchoolingDetailsForm = ({
  ceqData,
  examSchoolingDetailsData,
  birthCountryData,
  admissionLevelData,
  siblingExamLevelData,
  examTypesData,
  isOpsAdmin = false,
}: ExamSchoolingDetailsFormProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();
  const { uuid } = useParams<{ uuid: string }>();
  const { state } = useLocation<ExamSchoolingDetailsPageLocation>();
  const authorities = useGetUserAuthorities();
  const hasViewAccess = isOpsAdmin
    ? existInArray(authorities.data, AUTHORITY_NAMES.CP_ADMIN_NON_NATIONAL_APPLICATIONS_REVIEW_VIEW)
    : true;

  const hasEditAccess = isOpsAdmin
    ? existInArray(authorities.data, AUTHORITY_NAMES.CP_ADMIN_NON_NATIONAL_APPLICATIONS_REVIEW_EDIT)
    : true;

  const [isFullForm, setIsFullForm] = useState(
    admissionLevelData.ceqAdmissionLevels.includes(admissionLevelData.selectedAdmissionLevel)
  );
  const [selectedAdmissionLevel, setSelectedAdmissionLevel] = useState(
    admissionLevelData.selectedAdmissionLevel
  );
  const [validCeqTestDates, setValidCeqTestDates] = useState<ValidCeqTestDatesModel>();

  const ceqTypeDefault = checkCeqTypeDefault(examSchoolingDetailsData, ceqData);
  const countryDefault = checkCountryDefault({
    examSchoolingDetailsData,
    birthCountryData,
  });

  const {
    formHook,
    postExamSchoolingDetailStatus,
    hasSibling,
    hasStudyingInSingapore,
    isDisableAddSchool,
    isDisableAddSibling,
    schoolEmptyMessage,
    schools,
    setHasSibling,
    setHasStudyingInSingapore,
    setSchoolEmptyMessage,
    setSchools,
    setSiblingEmptyMessage,
    setSiblings,
    siblingEmptyMessage,
    siblings,
    admissionLevel,
    isAeis,
    submitButtonClick,
    createErrorMessage,
    isErrorPost,
    postError,
  } = useExamSchoolingDetails({
    uuid,
    isFullForm,
    ceqTypeDefault,
    examSchoolingDetailsData,
    countryDefault,
    selectedAdmissionLevel: examSchoolingDetailsData.selectedAdmissionLevel,
    birthCountryData,
    siblingExamLevelData,
    isOpsAdmin,
    selectedAdmissionLevelDescription: selectedAdmissionLevel,
    hasViewAccess,
    hasEditAccess,
    validCeqTestDates,
  });

  useEffect(() => {
    const newAdmissionLevel =
      examTypesData.admissionLevelVacancies.find(
        (admissionLevelVacancy) => admissionLevelVacancy.code === admissionLevel
      )?.description || admissionLevel;

    setIsFullForm(admissionLevelData.ceqAdmissionLevels.includes(newAdmissionLevel));
    setSelectedAdmissionLevel(newAdmissionLevel);
  }, [
    admissionLevel,
    examTypesData.admissionLevelVacancies,
    admissionLevelData.ceqAdmissionLevels,
  ]);

  const { formState: { errors }, watch } = formHook;
  const { dateOfCEQ: dateOfCEQWatch } = watch();
  const [isDateOutOfRange, setIsDateOutOfRange] = useState(false);
  const handleSubmitButtonClick = () => {
    const selectedDate = dayjs(watch('dateOfCEQ'));
    if (validCeqTestDates) {
      const { firstAcceptedCeqTestDate, lastAcceptedCeqTestDate } = validCeqTestDates;
      const firstAcceptedDate = dayjs(firstAcceptedCeqTestDate);
      const lastAcceptedDate = dayjs(lastAcceptedCeqTestDate);

      if (selectedDate.isBefore(firstAcceptedDate) || selectedDate.isAfter(lastAcceptedDate)) {
        setIsDateOutOfRange(true);
        return; // Prevent form submission if validation fails
      }
    }
    setIsDateOutOfRange(false);
    submitButtonClick();
  };

  return (
    <>
      <AdmissionLevelSection
        formHook={formHook}
        options={examTypesData?.admissionLevelVacancies || []}
        selectedAdmissionLevel={admissionLevelData?.selectedAdmissionLevel || '-'}
        isOpsAdmin={isOpsAdmin}
        isAeis={isAeis}
        hasEditAccess={hasEditAccess}
      />
      {isAeis && (
        <MotherTongueLanguageSection
          formHook={formHook}
          selectedMotherTongueLanguage={examSchoolingDetailsData?.motherTongueLanguageToBeTaken}
          hasEditAccess={hasEditAccess}
        />
      )}
      {isFullForm && (
        <Box className={classes.container}>
          <ContentTemplate isDividerBottom continuous>
            <CAECertificateDetails
              setValidCeqTestDates={setValidCeqTestDates}
              registrationUuid={uuid}
              registrationExamLevel={examTypesData?.examType}
              ceqData={ceqData}
              formHook={formHook}
              dateOfCEQ={dateOfCEQWatch}
              birthCountryData={birthCountryData}
              hasEditAccess={hasEditAccess}
              isFullForm={isFullForm}
            />
            <CEQScores formHook={formHook} hasEditAccess={hasEditAccess} />
          </ContentTemplate>
        </Box>
      )}
      <Box className={classes.container}>
        <ContentTemplate type="full" continuous>
          <Typography className={classes.marginSection} variant="h3">
            {t('exam->>NonNationalExam.SchoolingDetails')}
          </Typography>
          <RadioButtonView
            classes={classes}
            formHook={formHook}
            hasValue={hasStudyingInSingapore}
            setHas={setHasStudyingInSingapore}
            t={t}
            title={
              isAeis
                ? t('exam->>NonNationalExam.IsStudyingInSingapore')
                : t('exam->>NonNationalExam.AreYouCurrentlyStudying')
            }
            name="hasStudyingInSingapore"
            setEmptyMessage={setSchoolEmptyMessage}
            hasEditAccess={hasEditAccess}
          />
          {errors.hasStudyingInSingapore?.message && !hasStudyingInSingapore && (
            <Typography component="p" variant="inherit" className={classes.errorText}>
              {errors.hasStudyingInSingapore?.message}
            </Typography>
          )}
          {hasStudyingInSingapore === 'yes' && (
            <SchoolInfoTable
              data={schools}
              setSchools={setSchools}
              birthCountryData={birthCountryData || []}
              isDisableAdd={isDisableAddSchool || !hasEditAccess}
              isShowAction={hasEditAccess}
              isAEIS
            />
          )}
          {schoolEmptyMessage && (
            <Typography component="p" variant="inherit" className={classes.errorText}>
              {schoolEmptyMessage}
            </Typography>
          )}
          <RadioButtonView
            classes={classes}
            formHook={formHook}
            hasValue={hasSibling}
            setHas={setHasSibling}
            t={t}
            title={
              t('exam->>NonNationalExam.SiblingRegisteringQuestStart') +
              state?.examType.replace('_', '-') +
              t('exam->>NonNationalExam.SiblingRegisteringQuestEnd')
            }
            name="hasSibling"
            setEmptyMessage={setSiblingEmptyMessage}
            hasEditAccess={hasEditAccess}
          />
          {errors.hasSibling?.message && !hasSibling && (
            <Typography component="p" variant="inherit" className={classes.errorText}>
              {errors.hasSibling?.message}
            </Typography>
          )}
          {hasSibling === 'yes' && (
            <SiblingInfoTable
              data={siblings}
              levels={siblingExamLevelData}
              setSiblings={setSiblings}
              isDisableAdd={isDisableAddSibling}
              isShowAction={hasEditAccess}
              hasEditAccess={hasEditAccess}
            />
          )}
          {siblingEmptyMessage && (
            <Typography component="p" variant="inherit" className={classes.errorText}>
              {siblingEmptyMessage}
            </Typography>
          )}

          {isDateOutOfRange && (
            <Typography variant="body2" className={classes.errorText}>
              CEQ date must be between the accepted range.
            </Typography>
          )}
          <ButtonContainer
            classes={classes}
            examSchoolingDetailsData={examSchoolingDetailsData}
            goBackClick={history.goBack}
            postExamSchoolingDetailStatus={postExamSchoolingDetailStatus}
            submitButtonClick={handleSubmitButtonClick}
            t={t}
            createErrorMessage={createErrorMessage}
            isErrorPost={isErrorPost}
            postError={postError}
          />
        </ContentTemplate>
      </Box>
    </>
  );
};

export default ExamSchoolingDetailsForm;
